import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Browser geolocation, requested only once the reason is visible to the user.
 *
 * WHY IT IS NOT REQUESTED ON LOAD
 *
 * A permission prompt with no context is the one users reflexively dismiss —
 * and a dismissal is sticky: Chrome auto-denies subsequent requests from the
 * same origin, and there is no API to re-prompt. Burning the request on page
 * load therefore costs the capability permanently, for a user who never asked
 * for it. So nothing here fires until the model actually calls a tool that
 * needs a location, at which point the reason is obvious from what the user
 * just typed.
 *
 * WHY A GESTURE IS REQUIRED
 *
 * `getCurrentPosition` must be called from a user gesture to be reliable —
 * Safari requires one outright, and Chrome degrades to silent denial when a
 * prompt appears without one. A tool-call event arriving over SSE is not a
 * gesture. The consent chip exists to supply one: the click that says "yes"
 * IS the gesture, which is why permission cannot be requested straight off the
 * model's decision.
 */

export type GeolocationStatus =
  | "unsupported"
  | "idle"
  | "prompting"
  | "granted"
  | "denied"
  | "error";

export interface Coordinates {
  latitude: number;
  longitude: number;
  /** Metres. Useful for deciding whether a fix is precise enough to use. */
  accuracy: number;
  /** ms epoch — a cached fix can be stale enough to be wrong. */
  timestamp: number;
}

export interface UseGeolocationResult {
  status: GeolocationStatus;
  coords: Coordinates | null;
  error: string | null;
  /**
   * Request a position. MUST be called from a user gesture (a click handler),
   * not from an effect or a stream event — see the note above.
   */
  request: () => Promise<Coordinates | null>;
  /** Clear a stored fix, e.g. when the user revokes consent in-app. */
  clear: () => void;
}

/** A fix older than this is re-requested rather than reused. */
const MAX_AGE_MS = 5 * 60 * 1000;

export function useGeolocation(): UseGeolocationResult {
  const supported =
    typeof navigator !== "undefined" && "geolocation" in navigator;

  const [status, setStatus] = useState<GeolocationStatus>(
    supported ? "idle" : "unsupported",
  );
  const [coords, setCoords] = useState<Coordinates | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Guards against setting state after unmount — the permission prompt can sit
  // open indefinitely while the user decides, and they may navigate away.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Reflect an already-granted permission without prompting. Only reads state —
  // the Permissions API cannot trigger a prompt, so this is safe on mount.
  useEffect(() => {
    if (!supported || !navigator.permissions?.query) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: "geolocation" as PermissionName })
      .then((res) => {
        if (cancelled || !mountedRef.current) return;
        if (res.state === "granted") setStatus("granted");
        else if (res.state === "denied") setStatus("denied");
      })
      .catch(() => {
        // Permissions API unavailable (older Safari) — stay "idle" and let the
        // request itself reveal the state.
      });
    return () => {
      cancelled = true;
    };
  }, [supported]);

  const request = useCallback((): Promise<Coordinates | null> => {
    if (!supported) {
      setStatus("unsupported");
      return Promise.resolve(null);
    }

    // Reuse a recent fix rather than re-prompting the device for one.
    if (coords && Date.now() - coords.timestamp < MAX_AGE_MS) {
      return Promise.resolve(coords);
    }

    setStatus("prompting");
    setError(null);

    return new Promise<Coordinates | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const next: Coordinates = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            timestamp: pos.timestamp,
          };
          if (mountedRef.current) {
            setCoords(next);
            setStatus("granted");
          }
          resolve(next);
        },
        (err) => {
          if (mountedRef.current) {
            // PERMISSION_DENIED (1) is a user decision, not a fault — keep it
            // distinct so the UI can stop asking rather than showing an error.
            setStatus(err.code === err.PERMISSION_DENIED ? "denied" : "error");
            setError(err.message || "Could not determine location");
          }
          resolve(null);
        },
        {
          enableHighAccuracy: false, // city-level is enough; avoids GPS spin-up
          timeout: 10_000,
          maximumAge: MAX_AGE_MS,
        },
      );
    });
  }, [supported, coords]);

  const clear = useCallback(() => {
    setCoords(null);
    setError(null);
    setStatus(supported ? "idle" : "unsupported");
  }, [supported]);

  return { status, coords, error, request, clear };
}

/**
 * Tools whose results depend on where the user physically is.
 *
 * Deliberately a small explicit list rather than a name heuristic: matching on
 * something like /weather|local/ would fire the consent chip for
 * `live-data__wikipedia` searches about weather, which is exactly the kind of
 * unexplained permission prompt this design exists to avoid.
 */
export const LOCATION_AWARE_TOOLS = new Set<string>([
  "live-data__weather",
  "live-data__air_quality",
  "live-data__sunrise_sunset",
  "live-data__earthquakes",
  "live-data__tfl_stops",
  "live-data__tfl_arrivals",
]);

export function isLocationAwareTool(toolName: string): boolean {
  return LOCATION_AWARE_TOOLS.has(toolName);
}
